/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import { type RefObject, useEffect, useRef, useState } from 'react';

import { Tooltip } from '../tooltip';

const useIsTruncated = (
  text: string,
): [RefObject<HTMLSpanElement | null>, boolean] => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (element === null) {
      return;
    }

    // Reads inside the ResizeObserver callback are layout-synced and cheap;
    // setState with an unchanged boolean does not trigger a re-render.
    const check = () =>
      setIsTruncated(element.scrollWidth > element.clientWidth);

    check();
    const observer = new ResizeObserver(check);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);

  return [ref, isTruncated];
};

type TruncatedTextWithTooltipProps = {
  /** The text to display. Truncated with an ellipsis when space is limited. */
  text: string;
  /** Class name applied to the text element. Typography is inherited from the parent. */
  className?: string;
  /** Placement of the tooltip showing the full text. */
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
};

/**
 * Internal component that renders single-line truncating text and, only while
 * the text is actually clipped, shows a tooltip with the full text.
 *
 * Accessibility: the text element receives `tabIndex={0}` only while truncated,
 * so sighted keyboard users can reveal the full text on focus without adding a
 * dead tab stop otherwise. No button semantics are used since there is no
 * action to perform. Screen readers always receive the full text from the
 * element's own content, as CSS truncation is visual-only. The tooltip is
 * dismissible with Escape and hoverable (WCAG 1.4.13).
 *
 * @internal - Not part of the public API.
 */
export const TruncatedTextWithTooltip = ({
  text,
  className,
  tooltipPlacement = 'top-start',
}: TruncatedTextWithTooltipProps) => {
  const [textRef, isTruncated] = useIsTruncated(text);

  return (
    <Tooltip
      type="simple"
      placement={tooltipPlacement}
      isDisabled={!isTruncated}
    >
      <Tooltip.Trigger hasButtonWrapper>
        <span
          ref={textRef}
          className={classNames('ndl-truncated-text', className)}
          {...(isTruncated && { tabIndex: 0 })}
        >
          {text}
        </span>
      </Tooltip.Trigger>
      <Tooltip.Content>{text}</Tooltip.Content>
    </Tooltip>
  );
};
